import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Loading, Card } from "@/components/Shared";
import { RecordLayout, DetailPanel, Field } from "@/components/Record";
import { RecordTabs } from "@/components/Activities";
import { formatINR } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { ChevronLeft, Building2, Truck, Package, ChevronRight } from "lucide-react";

const STATUS = { Draft: "bg-[#EEF0F5] text-[#6B7189]", Sent: "bg-[#EAF3FF] text-[#1D4ED8]", "Partially Received": "bg-[#FEF3C7] text-[#B45309]", Received: "bg-[#DCFCE7] text-[#15803D]", Cancelled: "bg-[#FEE2E2] text-[#B91C1C]" };
const fdate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—");
const Stat = ({ label, value }) => (<div><div className="text-[11px] text-[#6B7189]">{label}</div><div className="text-lg font-bold text-[#101D51]">{value}</div></div>);

export default function SupplierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("pos");
  useEffect(() => { api.get(`/suppliers/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))); }, [id]);
  if (!d) return <Loading />;
  const s = d.supplier, st = d.stats;
  const tabs = [
    { id: "pos", label: "Purchase orders", icon: Truck, count: d.purchase_orders.length },
    { id: "products", label: "Products supplied", icon: Package, count: d.products.length },
  ];
  return (
    <div data-testid="supplier-detail">
      <button onClick={() => navigate("/inventory?tab=suppliers")} className="flex items-center gap-1 text-sm text-[#101D51] mb-3" data-testid="sup-back"><ChevronLeft className="w-4 h-4" /> Suppliers</button>
      <div className="bg-white border border-[#E6E8F0] rounded-xl px-4 sm:px-5 py-3 mb-4 shadow-sm flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#1D4ED8] flex items-center justify-center text-white shrink-0"><Building2 className="w-5 h-5" /></div>
        <div className="min-w-0 flex-1"><div className="text-xs text-[#6B7189]">Supplier</div><h1 className="text-xl sm:text-2xl font-bold text-[#101D51] truncate">{s.name}</h1></div>
      </div>
      <RecordLayout
        left={<>
          <DetailPanel title="Supplier">
            <Field label="Name" value={s.name} />
            <Field label="Contact person" value={s.contact_person} />
            <Field label="Phone" value={s.phone} />
            <Field label="Email" value={s.email} />
            <Field label="GSTIN" value={s.gstin} mono />
            <Field label="Address" value={s.address} />
          </DetailPanel>
          <Card className="p-4 grid grid-cols-3 gap-3" data-testid="sup-stats">
            <Stat label="POs" value={st.total_pos} />
            <Stat label="Open" value={st.open_pos} />
            <Stat label="Value" value={formatINR(st.total_value_paise)} />
          </Card>
        </>}
        right={<>
          <RecordTabs tabs={tabs} active={tab} onChange={setTab} testid="sup-tabs" />
          {tab === "pos" && (
            <Card className="overflow-hidden" data-testid="sup-pos">
              {d.purchase_orders.length === 0 ? <div className="p-5 text-sm text-[#6B7189]">No purchase orders raised to this supplier yet.</div> : (
                <div className="overflow-x-auto"><table className="w-full text-sm">
                  <thead className="bg-[#F5F6FA] text-[#6B7189]"><tr className="text-left">
                    <th className="px-4 py-2 font-medium">PO #</th><th className="px-4 py-2 font-medium">Date</th>
                    <th className="px-4 py-2 font-medium text-center">Status</th><th className="px-4 py-2 font-medium text-right">Total</th><th className="px-4 py-2"></th>
                  </tr></thead>
                  <tbody>{d.purchase_orders.map((po) => (
                    <tr key={po.id} className="border-t border-[#F0F1F5] hover:bg-[#F5F6FA] cursor-pointer" onClick={() => navigate(`/inventory/purchase-orders/${po.id}`)} data-testid={`sup-po-${po.id}`}>
                      <td className="px-4 py-2 font-medium text-[#1D4ED8]">{po.number}</td>
                      <td className="px-4 py-2 text-[#6B7189]">{fdate(po.date)}</td>
                      <td className="px-4 py-2 text-center"><span className={`text-[10px] rounded-full px-2 py-0.5 ${STATUS[po.status] || "bg-[#F5F6FA] text-[#6B7189]"}`}>{po.status}</span></td>
                      <td className="px-4 py-2 text-right font-semibold text-[#101D51]">{formatINR(po.grand_total)}</td>
                      <td className="px-4 py-2 text-right"><ChevronRight className="w-4 h-4 text-[#6B7189]" /></td>
                    </tr>))}</tbody>
                </table></div>)}
            </Card>)}
          {tab === "products" && (
            <Card className="p-4" data-testid="sup-products">
              {d.products.length === 0 ? <div className="text-sm text-[#6B7189]">No products purchased from this supplier yet.</div> : (
                <div className="space-y-1.5">{d.products.map((p) => (
                  <div key={p.code} className="flex items-center justify-between bg-[#F8F9FC] rounded-lg px-3 py-2 text-sm">
                    <span className="text-[#101D51]">{p.name}</span><span className="text-[11px] text-[#6B7189] font-mono">{p.code}</span>
                  </div>))}</div>)}
            </Card>)}
        </>} />
    </div>);
}
